if (!customElements.get('where-to-buy-directory')) {
  class WhereToBuyDirectory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.cards = Array.from(this.querySelectorAll('[data-channel-card]'));
      this.filters = this.querySelector('[data-channel-filters]');
      this.countrySelect = this.querySelector('[data-country-filter]');
      this.typeSelect = this.querySelector('[data-type-filter]');
      this.searchInput = this.querySelector('[data-search-filter]');
      this.clearButton = this.querySelector('[data-clear-filters]');
      this.resultCount = this.querySelector('[data-result-count]');
      this.noResults = this.querySelector('[data-no-results]');

      if (
        this.cards.length === 0 ||
        !this.filters ||
        !this.countrySelect ||
        !this.typeSelect ||
        !this.searchInput ||
        !this.clearButton
      ) {
        return;
      }

      this.initialized = true;
      this.abortController = new AbortController();
      this.countryParam = this.dataset.countryParam || 'country';
      this.typeParam = this.dataset.typeParam || 'type';
      this.filterParam = this.dataset.filterParam || 'filter';
      this.defaultCountry = this.normalizeValue(this.dataset.defaultCountry);

      this.populateSelect(this.countrySelect, 'country', 'countryLabel');
      this.populateSelect(this.typeSelect, 'channelType', 'channelTypeLabel');
      this.filters.hidden = false;
      this.syncFromUrl();

      const signal = this.abortController.signal;
      this.countrySelect.addEventListener('change', () => this.handleControlChange(), { signal });
      this.typeSelect.addEventListener('change', () => this.handleControlChange(), { signal });
      this.searchInput.addEventListener('input', () => this.handleControlChange(), { signal });
      this.clearButton.addEventListener('click', () => this.clearFilters(), { signal });
      window.addEventListener('popstate', () => this.syncFromUrl(), { signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.initialized = false;
    }

    normalizeValue(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase()
        .replace(/[_\s]+/g, '-');
    }

    populateSelect(select, valueKey, labelKey) {
      const options = new Map();

      this.cards.forEach((card) => {
        const value = this.normalizeValue(card.dataset[valueKey]);
        const label = String(card.dataset[labelKey] || '').trim();

        if (value && label && !options.has(value)) options.set(value, label);
      });

      const collator = new Intl.Collator(document.documentElement.lang || undefined, {
        sensitivity: 'base',
      });

      Array.from(options.entries())
        .sort((left, right) => collator.compare(left[1], right[1]))
        .forEach(([value, label]) => select.add(new Option(label, value)));
    }

    syncFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const requestedCountry = params.has(this.countryParam)
        ? params.get(this.countryParam)
        : this.defaultCountry;

      this.setSelectValue(this.countrySelect, requestedCountry, true);
      this.setSelectValue(this.typeSelect, params.get(this.typeParam));
      this.searchInput.value = String(params.get(this.filterParam) || '').trim();
      this.applyFilters();
    }

    setSelectValue(select, requestedValue, useCountryAliases = false) {
      const normalizedValue = this.normalizeValue(requestedValue);
      const availableValues = new Set(Array.from(select.options, (option) => option.value));
      const countryAliases = { gb: 'uk', uk: 'gb' };
      const aliasedValue = useCountryAliases ? countryAliases[normalizedValue] : '';

      if (availableValues.has(normalizedValue)) {
        select.value = normalizedValue;
      } else if (aliasedValue && availableValues.has(aliasedValue)) {
        select.value = aliasedValue;
      } else {
        select.value = '';
      }
    }

    handleControlChange() {
      this.applyFilters();
      this.updateUrl();
    }

    applyFilters() {
      const country = this.normalizeValue(this.countrySelect.value);
      const type = this.normalizeValue(this.typeSelect.value);
      const filter = this.searchInput.value.trim().toLocaleLowerCase();
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const matchesCountry = !country || this.normalizeValue(card.dataset.country) === country;
        const matchesType = !type || this.normalizeValue(card.dataset.channelType) === type;
        const matchesFilter = !filter || String(card.dataset.search || '').toLocaleLowerCase().includes(filter);
        const isVisible = matchesCountry && matchesType && matchesFilter;

        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      if (this.resultCount) {
        const template =
          visibleCount === 0
            ? this.dataset.resultNone
            : visibleCount === 1
              ? this.dataset.resultOne
              : this.dataset.resultMany;

        this.resultCount.textContent = String(template || '').replace('[count]', visibleCount);
      }

      if (this.noResults) this.noResults.hidden = visibleCount !== 0;
      this.clearButton.disabled = !country && !type && !filter;
    }

    updateUrl() {
      // Standalone local previews use the file: protocol, where History API URL
      // writes are not consistently permitted. Filtering still works without it.
      if (window.location.protocol === 'file:') return;

      const url = new URL(window.location.href);
      this.setUrlParam(url, this.countryParam, this.countrySelect.value);
      this.setUrlParam(url, this.typeParam, this.typeSelect.value);
      this.setUrlParam(url, this.filterParam, this.searchInput.value.trim());
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    setUrlParam(url, name, value) {
      if (value) {
        url.searchParams.set(name, value);
      } else {
        url.searchParams.delete(name);
      }
    }

    clearFilters() {
      this.countrySelect.value = '';
      this.typeSelect.value = '';
      this.searchInput.value = '';
      this.applyFilters();
      this.updateUrl();
      this.countrySelect.focus();
    }
  }

  customElements.define('where-to-buy-directory', WhereToBuyDirectory);
}
